import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function ExperienceSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const experiences = [
    {
      title: 'Capital & Legacy',
      subtitle: 'Day One',
      description: 'Closed-door conversations on private markets, cross-border structures, family constitutions, and governance frameworks.',
      icon: 'ri-funds-line',
      image: 'https://public.readdy.ai/ai/img_res/e68d6d3f54dfc0a84e3a0d6a12b473fd.jpg'
    },
    {
      title: 'Identity & Lifestyle',
      subtitle: 'Day Two',
      description: 'Designing a life that matches your values, culture, and global footprint through next-gen leadership and personal alignment.',
      icon: 'ri-compass-3-line',
      image: 'https://readdy.ai/api/search-image?query=sophisticated%20lifestyle%20scene%20elegant%20woman%20in%20beautiful%20contemporary%20space%20with%20art%20and%20refined%20design%20elements%20soft%20natural%20light%20minimalist%20luxury%20aesthetic%20cultural%20sophistication%20navy%20and%20champagne%20tones&width=800&height=600&seq=exp3&orientation=landscape'
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-32 bg-gradient-to-b from-white to-navy-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-champagne-600 text-sm font-sans font-medium tracking-[0.2em] uppercase">
            The Experience
          </span>
          
          <h2 className="font-display text-5xl lg:text-6xl font-bold text-navy-950 mt-6 mb-8 leading-[1.1]">
            Two Days of<br />Transformation
          </h2>
          
          <div className="w-16 h-0.5 bg-champagne-500 mx-auto"></div>
        </div>

        {/* Experience Cards */}
        <div className="grid lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
          {experiences.map((exp, index) => (
            <Link
              to="/program"
              key={index}
              className={`group relative overflow-hidden bg-white transition-all duration-700 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onMouseEnter={() => setActiveCard(index)}
              onMouseLeave={() => setActiveCard(null)}
            >
              {/* Image Container */}
              <div className="relative h-96 overflow-hidden">
                <img
                  src={exp.image}
                  alt={exp.title}
                  className={`w-full h-full object-cover transition-transform duration-700 ${activeCard === index ? 'scale-110' : 'scale-100'}`}
                />
                <div className={`absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/60 to-transparent transition-opacity duration-500 ${activeCard === index ? 'opacity-90' : 'opacity-70'}`}></div>
                
                {/* Icon */}
                <div className="absolute top-8 left-8">
                  <div className={`w-16 h-16 flex items-center justify-center bg-champagne-500/20 backdrop-blur-sm rounded-full border border-champagne-400/30 transition-all duration-500 ${activeCard === index ? 'scale-110 bg-champagne-500/30' : 'scale-100'}`}>
                    <i className={`${exp.icon} text-3xl text-champagne-400`}></i>
                  </div>
                </div>

                {/* Day Badge */}
                <div className="absolute top-8 right-8">
                  <span className="px-4 py-2 bg-champagne-500/20 backdrop-blur-sm rounded-full border border-champagne-400/30 text-champagne-300 text-sm font-sans font-medium tracking-wide">
                    {exp.subtitle}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-transform duration-500">
                <h3 className="font-display text-3xl font-bold text-white mb-4 leading-tight">
                  {exp.title}
                </h3>
                
                <p className={`text-navy-200 font-sans leading-relaxed transition-all duration-500 ${activeCard === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  {exp.description}
                </p>

                {/* Hover Indicator */}
                <div className={`mt-6 flex items-center gap-2 text-champagne-400 font-sans font-medium text-sm transition-all duration-500 ${activeCard === index ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
                  <span>Explore</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>

              {/* Bottom Accent */}
              <div className={`absolute bottom-0 left-0 w-full h-1 bg-champagne-500 transform transition-transform duration-500 origin-left ${activeCard === index ? 'scale-x-100' : 'scale-x-0'}`}></div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}